import { useState, useEffect } from "react";
import app from "nystem";
import SearchContext from "./searchContext";

const DatabaseSearchContextProvider = ({ children, view }) => {
  const [search, setSearchState] = useState({});
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const { contentType } = view;
    let search = {};
    let timer = false;
    let active = false;
    let mounted = true;
    let searchCount = 0;
    let retryNeeded = true;
    let retries = 0;
    let collection = app.database[contentType];

    const setSearchDebounce = async (newSearch) => {
      if (!mounted) return;
      if (!active) {
        setLoading(true);
        view.on("search", 200, doSearch);
        collection = app.database[contentType];
        if (!newSearch?.noAutoUpdate && collection) collection.on("update", -1000, setSearch);
        app.on(["login", "logout"], -1000, setSearch);
        active = true;
      }
      if (newSearch) {
        search = { ...newSearch, isDirty: true };
        retries = 0;
      }
      if (!timer && search.delay !== -1) timer = setTimeout(() => setSearch(), 0);
    };

    const setSearch = async () => {
      if (!mounted) return;
      clearTimeout(timer);
      timer = false;

      const count = ++searchCount;
      const pendingLog = setTimeout(
        () => app.event("clientLog", { message: "Search pending 10s", contentType }),
        10000,
      );
      let searchResult;
      try {
        searchResult = await view.event("search", { ...search, data: undefined });
      } catch (error) {
        searchResult = { ...search, error: error.message };
      }
      clearTimeout(pendingLog);
      // A slow reply must not replace the result of a newer search.
      if (!mounted || count !== searchCount) return searchResult;
      // An empty result (data false) is an answer, only a missing one is retried.
      retryNeeded = !!(searchResult.offline || searchResult.error || searchResult.data === undefined);
      if (retryNeeded)
        app.event("clientLog", {
          message: "Search failed",
          contentType,
          offline: searchResult.offline,
          error: searchResult.error,
          retries,
        });
      else if (!searchResult.data)
        app.event("clientLog", {
          message: "Search empty",
          contentType,
          filter: JSON.stringify(searchResult.filter),
          role: searchResult.role,
          total: searchResult.total,
        });
      setSearchState({ ...searchResult, isDirty: retryNeeded });

      setLoading(false);
      if (retryNeeded && app.connection.connected && search.delay !== -1 && retries < 2) {
        retries++;
        timer = setTimeout(setSearch, 1000);
      }
      return searchResult;
    };

    const onConnection = ({ connected }) => {
      if (!connected) {
        retryNeeded = true;
        clearTimeout(timer);
        timer = false;
      } else if (active && retryNeeded && search.delay !== -1) {
        retries = 0;
        setSearch();
      }
    };
    // Run after the database connection handler has cleared stale caches.
    app.connection.on("connection", -2000, onConnection);

    const getSearch = () => search;
    view.on("getSearch", getSearch);

    view.on("setSearch", -500, setSearchDebounce);

    const doSearch = (search) => app.database[contentType]?.search({ ...search, data: undefined });

    return () => {
      mounted = false;
      clearTimeout(timer);
      app.connection.off("connection", onConnection);
      view.off("setSearch", setSearchDebounce);

      if (active) {
        view.off("search", doSearch);
        collection?.off("update", setSearch);
        app.off(["login", "logout"], setSearch);
      }
      view.off("getSearch", getSearch);
    };
  }, [view]);

  return <SearchContext.Provider value={{ loading, search }}>{children}</SearchContext.Provider>;
};
export default DatabaseSearchContextProvider;
